import { faExclamationTriangle } from "@imaginelearning/mps-icon-library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IAlp, IAssignmentInfo, AssignmentStatusEnum } from "@imaginelearning/mps-api-service";
import {
  UxAvatar,
  UxAvatarEnum,
  UxAvatarPriorityEnum,
  UxAvatarStyleEnum,
  UxIcon,
  UxProgress,
  UxPriorityEnum
} from "@imaginelearning/mps-ux-component-library";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import "./assignment-coursemap-header.scss";
import useGradeRange from "hooks/usegraderange";
import useBreakpoint from "../liftoff/hooks/useBreakpoint";
import SmallScreenPanel from "../small-screen-panel/small-screen-panel";
import getCurrentLessonIcon, { getk5LessonIcon } from "./common/icons";
import utcDateHandler from "./common/date-convertor";
import useTheme from "../../hooks/usetheme";
export interface AssignmentCourseMapHeaderProps {
  lessonsCompleted: number;
  alp: IAlp;
}

const assignmentInfoDefaultData: IAssignmentInfo = {
  assignmentID: "",
  title: "",
  assignmentFolderID: "",
  assignmentFolderName: "",
  ilpID: "",
  createdBy: "",
  createdOn: "",
  dueDate: "",
  assignedDate: "",
  icon: "",
  score: 0,
  contentType: -1,
  passingThreshold: 0,
  lessons: [],
  assessments: []
};

const alpDefaultData: IAlp = {
  id: "",
  userId: "",
  gradeLevel: 4,
  placementGradeLevel: 2,
  subject: "",
  enabled: true,
  standardCode: "",
  source: "",
  state: "",
  createdOn: "",
  subjectTitle: "",
  productTitle: "",
  percentageComplete: 1,
  schoolYear: 2022,
  academicSessionId: "",
  assignmentInfo: assignmentInfoDefaultData,
  lessons: [],
  isCompleted: false,
  isNew: false,
  lessonAttempts: [],
  translationLanguage: ""
};

function AssignmentCourseMapHeader(props: AssignmentCourseMapHeaderProps) {
  const { isSmallScreen } = useBreakpoint();
  const navigate = useNavigate();
  const [past, setPast] = useState(false);
  const [subHeadIcon, setSubHeadIcon] = useState<string>("");
  const { alp } = props;
  const { theme } = useTheme();
  const { isKto2, isKto5, is6to12 } = useGradeRange();
  const selectedIcon = useMemo(
    () => (alp.assignmentInfo.icon ? (UxAvatarEnum as any)[alp.assignmentInfo.icon.toUpperCase()] : null),
    [alp]
  );

  const itemsCompleted =
    alp.assignmentInfo.contentType === 1
      ? alp.assignmentInfo.assessments?.filter((item: any) => item.status === AssignmentStatusEnum.COMPLETED).length
      : props.lessonsCompleted;
  const itemsLength =
    alp.assignmentInfo.contentType === 1 ? alp.assignmentInfo.assessments.length : alp.assignmentInfo.lessons.length;

  const UTCDueDateWithZ = utcDateHandler(alp.assignmentInfo.dueDate);
  const dateDifference = useCallback(() => {
    const dateGiven = new Date(UTCDueDateWithZ);
    const utc = new Date().toJSON().slice(0, 10).replace(/-/g, "/");
    const today = new Date(utc); // future Date
    const diffIntime = dateGiven.getTime() - today.getTime();
    const diffInDays = Math.trunc(diffIntime / (1000 * 3600 * 24));
    return diffInDays;
  }, [UTCDueDateWithZ]);
  const getIcon = isKto5 ? getk5LessonIcon : getCurrentLessonIcon;

  const dueDateRender = useMemo(() => {
    if (past) {
      return (
        <div className="due-date past-due">
          {" "}
          <FontAwesomeIcon icon={faExclamationTriangle} size="1x" /> Past Due!
        </div>
      );
    }
    const kto2DueDate = (diff: number) => {
      switch (diff) {
        case 0:
          return "Due Today";
        case 1:
          return `Due Tomorrow`;
        default:
          return `Due in ${diff} days`;
      }
    };
    const mydate = new Date(UTCDueDateWithZ);
    const options: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", year: "numeric" };
    const dueDateValue = isKto2
      ? kto2DueDate(dateDifference())
      : `Due Date: ${mydate.toLocaleDateString("en-us", options)}`;
    const dueDateHandle = UTCDueDateWithZ?.split("T")[0] === "9999-12-31" ? "" : dueDateValue;
    return <div className="due-date">{dueDateHandle}</div>;
  }, [past, dateDifference, UTCDueDateWithZ, isKto2]);

  useEffect(() => {
    const checkPastDue = () => {
      const diff = dateDifference();
      if (diff >= 0) {
        setPast(false);
      } else {
        setPast(true);
      }
    };
    checkPastDue();
  }, [dateDifference]);

  React.useEffect(() => {
    if (
      alp.subject.toLocaleLowerCase() === "math" ||
      alp.subject.toLocaleLowerCase() === "reading" ||
      (is6to12 && alp.subject.toLocaleLowerCase() === "science")
    ) {
      setSubHeadIcon(`abld_inverted_${alp.subject}`);
    }
    setSubHeadIcon(alp.subject.toLocaleLowerCase());
  }, [alp, is6to12]);
  const CapitalizeFirstLetter = (mySentence: string) =>
    mySentence.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());

  const titleAndSubject = useMemo(() => {
    const title = CapitalizeFirstLetter(alp.assignmentInfo.title);
    const subject = alp.subject === "SEL" ? "PURPOSE" : alp.subject.toUpperCase();
    return `${subject}: ${title}`;
  }, [alp]);

  const progressValue = itemsLength === 0 ? 0 : (itemsCompleted / itemsLength) * 100;

  if (isSmallScreen)
    return (
      <SmallScreenPanel
        onBackClick={() => {
          navigate(`/assignments/`);
        }}
      />
    );

  return (
    <div className="abcm-header">
      <div className="subject-icon">
        {" "}
        <span className="header-icon">
          <UxIcon
            icon={getIcon(subHeadIcon)}
            className="abld_coursemap_header_subject_icon"
            title="icon"
            theme={theme}
          />{" "}
        </span>
        <span className="assignment-title">{titleAndSubject}</span>
      </div>
      <div className={`abcm-header-right ${!alp.assignmentInfo.icon && "me-10"}`}>
        {UTCDueDateWithZ && dueDateRender}
        <div>
          <UxProgress
            variant={UxPriorityEnum.SUCCESS}
            value={progressValue}
            className="abld_coursemap_header_progress_bar"
          />
        </div>
        {alp.assignmentInfo.icon && (
          <div>
            <UxAvatar
              avatar={selectedIcon as UxAvatarEnum}
              avatarType={UxAvatarPriorityEnum.DEFAULT}
              avatarStyle={UxAvatarStyleEnum.BODY}
              className="d-flex abld_coursemap_header_avatar"
              theme={theme}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default AssignmentCourseMapHeader;
